import { useEffect, useState } from "react";
import { Controller, useFormState } from "react-hook-form";
import { Radio, Select } from "antd";
import InputMask from "react-input-mask";
import {
  BrandImage,
  CardBrands,
  Error,
  Input,
  Label,
  PhoneRow,
  RadioGroupContainer,
} from "./styles";
import { FormattedMessage, useIntl } from "react-intl";
import { AttentionIcon } from "../../../components/Icons";
import { formatFiscalId } from "./utils";
import masterBrand from "./master.png";
import eloBrand from "./elo.png";
import visaBrand from "./visa.png";
import americanBrand from "./american.png";
import auraBrand from "./aura.png";
import dinersBrand from "./diners.png";
import {
  getCountries,
  getCountryCallingCode,
} from "react-phone-number-input/input";
import en from "react-phone-number-input/locale/en.json";
import pt from "react-phone-number-input/locale/pt.json";
import flags from "react-phone-number-input/flags";
import {
  StyledDDISelect,
  FlagOption,
} from "../../../../../../../components/ModalNewCollaborator/styles";

const { Option } = Select;

export const FormInput = ({
  placeholder = "",
  name,
  control,
  rules,
  label,
  ...inputProps
}) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => (
        <div>
          {label && (
            <Label>
              <FormattedMessage id={label} />
            </Label>
          )}
          <Input
            {...field}
            {...inputProps}
            status={error ? "error" : ""}
            allowClear
            placeholder={placeholder}
            onChange={(e) => {
              const value = e.target.value;

              if (name === "document") {
                return field.onChange(formatFiscalId(value));
              }

              return field.onChange(value);
            }}
            maxLength={name === "cpf" ? 14 : 255}
          />
          {error && (
            <Error>
              <AttentionIcon color="#ff4d4f" size={16} />
              <FormattedMessage id={error.message} />
            </Error>
          )}
        </div>
      )}
    />
  );
};

export const FormRadioGroup = ({ name, control, rules, label }) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => (
        <RadioGroupContainer>
          {label && (
            <Label>
              <FormattedMessage id={label} />
            </Label>
          )}
          <Radio.Group {...field}>
            <Radio value="credit">
              <FormattedMessage id={"credit"} />
            </Radio>
            <Radio value="debit">
              <FormattedMessage id={"debit"} />
            </Radio>
          </Radio.Group>
          {error && (
            <Error>
              <AttentionIcon color="#ff4d4f" size={16} />
              <FormattedMessage id={error.message} />
            </Error>
          )}
        </RadioGroupContainer>
      )}
    />
  );
};

export const FormPhoneInput = ({ name, control, label, placeholder }) => {
  const intl = useIntl();
  const [countryList, setCountryList] = useState([]);
  const { errors } = useFormState({ control, name: ["ddi", name] });
  const ddiError = errors?.ddi;
  const phoneError = errors?.[name];
  const combinedError = ddiError || phoneError;

  useEffect(() => {
    const reactCountries = getCountries().map((country) => {
      const code = "+" + getCountryCallingCode(country);
      const countryName = intl.locale === "en" ? en[country] : pt[country];
      return {
        label: `${code} - ${countryName}`,
        value: code,
        name: countryName,
        key: `${code}-${country}`,
        countryCode: country,
      };
    });
    setCountryList(
      reactCountries.sort((a, b) => a.name.localeCompare(b.name))
    );
  }, [intl.locale]);

  return (
    <div>
      {label && (
        <Label>
          <FormattedMessage id={label} />
        </Label>
      )}
      <PhoneRow>
        <Controller
          name="ddi"
          control={control}
          render={({ field }) => (
            <div style={{ flexShrink: 0 }}>
              <StyledDDISelect
                {...field}
                status={ddiError ? "error" : ""}
                showSearch
                optionLabelProp="label"
                placeholder="DDI"
                dropdownMatchSelectWidth={340}
                filterOption={(input, option) => {
                  const searchTerm = input
                    .toLowerCase()
                    .replace(/[+\s-]/g, "");

                  const optionValue = (option?.filterLabel || "")
                    .toLowerCase()
                    .replace(/[+\s-]/g, "");

                  return optionValue.includes(searchTerm);
                }}
              >
                {countryList.map((country) => {
                  const Flag = flags[country.countryCode];
                  return (
                    <Option
                      key={country.key}
                      value={country.key}
                      filterLabel={country.label}
                      label={
                        <FlagOption>
                          {Flag && <Flag title={country.name} />}
                          {country.value}
                        </FlagOption>
                      }
                    >
                      <FlagOption>
                        {Flag && <Flag title={country.name} />}
                        {country.label}
                      </FlagOption>
                    </Option>
                  );
                })}
              </StyledDDISelect>
            </div>
          )}
        />

        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <div style={{ flex: 1, minWidth: 0 }}>
              <Input
                {...field}
                as={InputMask}
                mask="(99) 99999-9999"
                maskPlaceholder={null}
                status={phoneError ? "error" : ""}
                placeholder={placeholder}
              />
            </div>
          )}
        />
      </PhoneRow>
      {combinedError && (
        <Error>
          <AttentionIcon color="#ff4d4f" size={16} />
          <FormattedMessage id={combinedError.message} />
        </Error>
      )}
    </div>
  );
};

export const FormSelectBrand = ({ name, control, rules, label }) => {
  const brands = [
    {
      key: "master",
      label: "MasterCard",
      src: masterBrand,
    },
    {
      key: "visa",
      label: "Visa",
      src: visaBrand,
    },
    { key: "elo", label: "Elo", src: eloBrand },
    {
      key: "american",
      label: "American Express",
      src: americanBrand,
    },
    {
      key: "aura",
      label: "Aura",
      src: auraBrand,
    },
    {
      key: "diners",
      label: "Diners Club",
      src: dinersBrand,
    },
  ];
  return (
    <Controller
      name="brand"
      control={control}
      render={({ field, fieldState: { error } }) => (
        <CardBrands>
          <Label mb="16px">
            <FormattedMessage id="select_card_brand" />
          </Label>
          <div style={{ display: "flex", gap: "15px" }}>
            {brands.map(({ key, label, src }) => (
              <BrandImage
                key={key}
                src={src}
                alt={`${label} Brand`}
                onClick={() => field.onChange(label)}
                className={field.value === label ? "selected" : ""}
              />
            ))}
          </div>
          {error && (
            <Error>
              <AttentionIcon color="#ff4d4f" size={16} />
              <FormattedMessage id={error.message} />
            </Error>
          )}
        </CardBrands>
      )}
    />
  );
};
